import type { PImage } from '../../engine/aliases'
import type { IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { l } from '../../lib/imports'
import { rando } from '../../lib/rando-sidecar'
import { common } from '../../main/common-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import { voyageLayers } from './voyageLayers-sidecar'
import type {
  IVoyageParticleData,
  VoyageParticleType,
} from './voyageParticlesData'
import { voyageParticlesData } from './voyageParticlesData-sidecar'

export interface IVoyageParticle extends IEntity {
  data: IVoyageParticleData
  image: PImage
  speed: number
  dirX: number
  dirY: number
  fade: boolean
  shrink: boolean
  decelerate: boolean
  timeElapsed: number
  timeMax: number
  x: number
  y: number
  frameTimer: number
  frameIdx: number
  sineXoffset: number
}

const objs: IVoyageParticle[] = []

export const getAll = () => {
  return objs
}

export function createMultiple(
  type: VoyageParticleType,
  x: number,
  y: number,
  numParticles: number,
) {
  for (let i = 0; i < numParticles; i++) {
    create(type, x, y)
  }
}

export function create(
  type: VoyageParticleType,
  x: number,
  y: number,
): IVoyageParticle {
  let id = -1 // createId()
  let data = voyageParticlesData.get(type)

  let layer = voyageLayers.getLayers().particles

  let frame = data.frames[0]
  if (!data.playFrames) {
    frame = rando.sample(data.frames)
  }

  let image = spritesUtil.createImage(layer, frame)

  image.x = x
  image.y = y

  let minTime = data.minTimeSeconds || data.maxTimeSeconds * 0.5
  let timeMax = rando.f(minTime, data.maxTimeSeconds)
  let obj: IVoyageParticle = {
    eType: 'particle',
    data,
    isDead: false,
    id,
    idLoaded: 0,
    image,
    fade: false,
    shrink: false,
    dirX: rando.f(data.dirXrange[0], data.dirXrange[1]),
    dirY: rando.f(data.dirYrange[0], data.dirYrange[1]),
    speed: 100,
    decelerate: false,
    timeElapsed: 0,
    timeMax,
    x,
    y,
    frameTimer: 0,
    frameIdx: 0,
    sineXoffset: rando.f(0, Math.PI * 2),
  }
  image.flipX = rando.b()

  if (data.afterCreate) {
    data.afterCreate(obj)
  }

  // if (type === 'rain') {
  //   obj.dirX = 0
  //   obj.dirY = 1
  // }
  objs.push(obj)

  return obj
}

export function kill(obj: IVoyageParticle) {
  obj.isDead = true
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    c.image.destroy()
  })
}
export function updateAll() {
  objs.forEach((c) => {
    update(c)
  })
  removeDead()
}
function update(obj: IVoyageParticle) {
  let ds = common.getUpdateDeltaScaled()
  // let max = 0.01
  // if (d > max) {
  //   d = max
  // }

  let timeLeftFactor = 1 - obj.timeElapsed / obj.timeMax
  if (timeLeftFactor < 0) {
    timeLeftFactor = 0
  }
  if (obj.fade) {
    obj.image.alpha = timeLeftFactor
  }
  if (obj.shrink) {
    obj.image.scale = timeLeftFactor * obj.data.scale
  }

  let curSpeed = obj.speed
  if (obj.decelerate) {
    curSpeed = obj.speed * 1 - timeLeftFactor
  }

  let speed = curSpeed * ds

  let dx = obj.dirX * speed
  let dy = obj.dirY * speed

  if (obj.data.playFrames) {
    let frameRate = obj.timeMax / obj.data.frames.length
    obj.frameTimer += ds
    if (obj.frameTimer > frameRate) {
      obj.frameTimer -= frameRate
      obj.frameIdx++
      if (obj.frameIdx >= obj.data.frames.length) {
        kill(obj)
        return
      }
      spritesUtil.setPackedFrame(obj.image, obj.data.frames[obj.frameIdx])
    }
  }

  obj.dirY += obj.data.accelY * ds

  obj.x += dx
  obj.y += dy
  obj.image.x = obj.x
  obj.image.y = obj.y

  if (obj.data.sineX) {
    obj.image.x +=
      obj.data.sineX *
      Math.sin(common.getElapsedTime() * obj.data.sineXperiod + obj.sineXoffset)
  }

  obj.timeElapsed += ds

  if (obj.timeElapsed > obj.timeMax) {
    kill(obj)
  }
}
